import { useState, useRef } from "react";
import { VehicleStatusBadge } from "../VehicleStatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Car, Edit2, Trash2, Loader2, Search, Eye, EyeOff, MapPin, ImagePlus, X } from "lucide-react";
import api, { resolveImageUrl } from "../../lib/api";

const CAMEROON_CITIES = ["Yaounde", "Douala", "Bafoussam", "Bamenda", "Garoua", "Maroua", "Ngaoundere", "Bertoua", "Ebolowa", "Kribi", "Limbe", "Buea", "Kumba", "Nkongsamba", "Edea", "Loum", "Dschang", "Foumban", "Kousseri", "Mokolo"];

export default function VehicleManagement({ vehicles, onRefresh, onDelete, actionLoading }) {
  const [search, setSearch] = useState("");
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageDeleteTarget, setImageDeleteTarget] = useState(null);
  const [imageDeleteLoading, setImageDeleteLoading] = useState(false);
  const imageInputRef = useRef(null);

  const openEdit = (v) => {
    setEditForm({
      brand: v.brand || "", model: v.model || "", year: v.year || 2024,
      color: v.color || "", plate_number: v.plate_number || "",
      vehicle_type: v.vehicle_type || "sedan", city: v.city || "",
      price_per_day: v.price_per_day || 0,
      price_city_tour: v.price_city_tour || 0,
      price_out_of_city: v.price_out_of_city || 0,
      price_per_hour: v.price_per_hour || 0,
      seats: v.seats || 4, air_conditioning: v.air_conditioning || false,
      fuel_policy: v.fuel_policy || "not_included", mileage: v.mileage || "",
      vehicle_condition: v.vehicle_condition || "bon_etat",
      music: v.music || false, phone_charger: v.phone_charger || false,
      available: v.available !== false,
      operational_status_manual: v.operational_status_manual || (v.available !== false ? "available" : "unavailable"),
    });
    setEditDialog(v);
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    setEditLoading(true);
    try {
      await api.put(`/admin/vehicles/${editDialog.id}`, editForm);
      setEditDialog(null);
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setEditLoading(false); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/vehicles/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setDeleteLoading(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editDialog) return;
    if (file.size > 10 * 1024 * 1024) { alert("Fichier trop volumineux (max 10MB)"); return; }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/admin/vehicles/${editDialog.id}/upload-image`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setEditDialog({ ...editDialog, image_urls: data.image_urls });
      onRefresh?.();
    } catch (err) { alert(err.response?.data?.detail || "Erreur upload"); }
    finally { setImageUploading(false); if (imageInputRef.current) imageInputRef.current.value = ""; }
  };

  const handleImageDelete = async (imagePath) => {
    if (!editDialog || !imagePath) return;
    setImageDeleteLoading(true);
    try {
      const { data } = await api.delete(`/admin/vehicles/${editDialog.id}/delete-image`, { data: { image_path: imagePath } });
      setEditDialog((prev) => prev ? { ...prev, image_urls: data.image_urls } : prev);
      setImageDeleteTarget(null);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la suppression de l'image");
    } finally {
      setImageDeleteLoading(false);
    }
  };


  const filtered = vehicles.filter(v => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (v.brand?.toLowerCase().includes(s) || v.model?.toLowerCase().includes(s) || v.plate_number?.toLowerCase().includes(s) || v.owner_name?.toLowerCase().includes(s));
  });

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher (marque, modele, plaque, proprietaire)" className="pl-9 h-9 border-[#CBD5E1] text-sm" data-testid="admin-vehicle-search" />
        </div>
        <span className="text-xs text-[#64748B]">{filtered.length} vehicule(s)</span>
      </div>

      {/* Vehicles Table */}
      <div className="bg-white rounded-xl border border-[#CBD5E1] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#CBD5E1] bg-[#F8FAFC]">
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Vehicule</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Proprietaire</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Plaque</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Prix/jour</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Statut</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-[#64748B] text-sm">Aucun vehicule trouve</td></tr>
              ) : filtered.map((v) => (
                <tr key={v.id} className="border-b border-[#CBD5E1] last:border-0" data-testid={`admin-vehicle-${v.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {(v.image_urls?.[0] || v.image_url) ? (
                        <img src={v.image_urls?.[0] || v.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#F8FAFC] flex items-center justify-center"><Car className="w-5 h-5 text-[#CBD5E1]" /></div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-[#0A2540]">{v.brand} {v.model}</p>
                        <p className="text-xs text-[#64748B]">{v.year} | {v.color} | {v.vehicle_type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-sm text-[#0A2540]">{v.owner_name || "N/A"}</td>
                  <td className="p-3 text-sm text-[#64748B] font-mono">{v.plate_number}</td>
                  <td className="p-3">
                    <div>
                      <span className="text-sm font-bold text-[#0A2540]">{v.price_per_day?.toLocaleString()} XAF/j</span>
                      {v.price_per_hour > 0 && <span className="text-xs text-[#3B82F6] block">{v.price_per_hour.toLocaleString()} XAF/h</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-0.5">
                      <VehicleStatusBadge status={v.operational_status} />
                      {v.city && <span className="text-[10px] text-[#64748B] flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{v.city}</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)} className="h-7 px-2 text-xs text-[#0A2540]" data-testid={`admin-edit-vehicle-${v.id}`}>
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(v)} className="h-7 px-2 text-xs text-[#EF4444]" data-testid={`admin-delete-vehicle-${v.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Vehicle Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) { setEditDialog(null); setImageDeleteTarget(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-edit-vehicle-dialog">
          <DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Modifier le vehicule</DialogTitle></DialogHeader>
          {editDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Marque</label>
                  <Input value={editForm.brand} onChange={e => setEditForm({...editForm, brand: e.target.value})} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-brand" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Modele</label>
                  <Input value={editForm.model} onChange={e => setEditForm({...editForm, model: e.target.value})} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-model" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Annee</label>
                  <Input type="number" value={editForm.year} onChange={e => setEditForm({...editForm, year: parseInt(e.target.value) || 2024})} className="h-9 border-[#CBD5E1] mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Couleur</label>
                  <Input value={editForm.color} onChange={e => setEditForm({...editForm, color: e.target.value})} className="h-9 border-[#CBD5E1] mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Plaque</label>
                  <Input value={editForm.plate_number} onChange={e => setEditForm({...editForm, plate_number: e.target.value})} className="h-9 border-[#CBD5E1] mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Type</label>
                  <Select value={editForm.vehicle_type} onValueChange={v => setEditForm({...editForm, vehicle_type: v})}>
                    <SelectTrigger className="h-9 border-[#CBD5E1] mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedan">Berline</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="4x4">4x4</SelectItem>
                      <SelectItem value="minibus">Minibus</SelectItem>
                      <SelectItem value="pickup">Pick-up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Ville</label>
                  <Select value={editForm.city || "none"} onValueChange={v => setEditForm({...editForm, city: v === "none" ? "" : v})}>
                    <SelectTrigger className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-city"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non definie</SelectItem>
                      {CAMEROON_CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Prix/jour (XAF)</label>
                  <Input type="number" value={editForm.price_per_day} onChange={e => setEditForm({...editForm, price_per_day: parseInt(e.target.value) || 0})} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-price" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Prix/heure (XAF)</label>
                  <Input type="number" value={editForm.price_per_hour} onChange={e => setEditForm({...editForm, price_per_hour: parseInt(e.target.value) || 0})} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-price-hour" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Prix Location ville (XAF)</label>
                  <Input type="number" value={editForm.price_city_tour} onChange={e => setEditForm({...editForm, price_city_tour: parseInt(e.target.value) || 0})} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-price-city" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Prix Sortie de ville (XAF)</label>
                  <Input type="number" value={editForm.price_out_of_city} onChange={e => setEditForm({...editForm, price_out_of_city: parseInt(e.target.value) || 0})} className="h-9 border-[#CBD5E1] mt-1" data-testid="edit-vehicle-price-out" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Places</label>
                  <Input type="number" value={editForm.seats} onChange={e => setEditForm({...editForm, seats: parseInt(e.target.value) || 4})} className="h-9 border-[#CBD5E1] mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Etat</label>
                  <Select value={editForm.vehicle_condition} onValueChange={v => setEditForm({...editForm, vehicle_condition: v})}>
                    <SelectTrigger className="h-9 border-[#CBD5E1] mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neuf">Neuf</SelectItem>
                      <SelectItem value="bon_etat">Bon etat</SelectItem>
                      <SelectItem value="acceptable">Acceptable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Kilometrage</label>
                  <Input value={editForm.mileage} onChange={e => setEditForm({...editForm, mileage: e.target.value})} placeholder="Ex: 45000km" className="h-9 border-[#CBD5E1] mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Carburant</label>
                  <Select value={editForm.fuel_policy} onValueChange={v => setEditForm({...editForm, fuel_policy: v})}>
                    <SelectTrigger className="h-9 border-[#CBD5E1] mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="included">Inclus</SelectItem>
                      <SelectItem value="not_included">Non inclus</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#0A2540]">Statut du véhicule</label>
                  <Select value={editForm.operational_status_manual || (editForm.available === false ? "unavailable" : "available")}
                          onValueChange={v => setEditForm({...editForm, operational_status_manual: v, available: v !== "unavailable"})}>
                    <SelectTrigger className="h-9 border-[#CBD5E1] mt-1" data-testid="admin-vehicle-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> Disponible</span></SelectItem>
                      <SelectItem value="on_trip"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#EAB308]" /> En course</span></SelectItem>
                      <SelectItem value="unavailable"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#EF4444]" /> Indisponible</span></SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#64748B] mt-1">Note: si une course est active, le statut &quot;En course&quot; est auto-imposé.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 pt-1">
                {[
                  { key: "air_conditioning", label: "Climatisation" },
                  { key: "music", label: "Musique" },
                  { key: "phone_charger", label: "Chargeur tel" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editForm[key] || false} onChange={e => setEditForm({...editForm, [key]: e.target.checked})} className="rounded border-[#CBD5E1] text-[#0A2540]" />
                    <span className="text-xs text-[#0A2540]">{label}</span>
                  </label>
                ))}
              </div>
              {/* Image Management */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-semibold text-[#0A2540] mb-2">Images du vehicule</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(editDialog?.image_urls || []).map((imgPath, i) => {
                    const url = resolveImageUrl(imgPath);
                    return (
                      <div key={`img-${i}`} className="relative w-20 h-16 rounded-lg overflow-hidden border border-gray-200 group">
                        <img src={url} alt={`Image ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='64'><rect fill='%23f1f5f9' width='80' height='64'/><text x='40' y='36' text-anchor='middle' fill='%2394a3b8' font-size='10'>?</text></svg>"; }} />
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImageDeleteTarget(imgPath); }}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
                          data-testid={`delete-img-${i}`}
                          aria-label="Supprimer cette image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                  <button onClick={() => imageInputRef.current?.click()} disabled={imageUploading} className="w-20 h-16 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-[#FACC15] hover:text-[#0A2540] transition-colors" data-testid="admin-add-vehicle-image">
                    {imageUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                    <span className="text-[9px] mt-0.5">{imageUploading ? "..." : "Ajouter"}</span>
                  </button>
                  <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </div>
                {/* In-dialog confirmation for image deletion (avoids window.confirm which would dismiss the parent Radix dialog on mobile) */}
                {imageDeleteTarget && (
                  <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 flex items-center justify-between gap-3" data-testid="confirm-delete-image-bar">
                    <p className="text-xs text-red-700 flex-1">Supprimer cette image définitivement ?</p>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setImageDeleteTarget(null)}
                        className="h-7 px-2 text-xs border-[#CBD5E1]"
                        disabled={imageDeleteLoading}
                        data-testid="cancel-delete-image"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleImageDelete(imageDeleteTarget)}
                        disabled={imageDeleteLoading}
                        className="h-7 px-3 text-xs bg-red-500 text-white hover:bg-red-600"
                        data-testid="confirm-delete-image"
                      >
                        {imageDeleteLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                        Supprimer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={saveEdit} disabled={editLoading} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold mt-2" data-testid="save-admin-vehicle-edit">
                {editLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Edit2 className="w-4 h-4 mr-1" />} Enregistrer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="admin-delete-vehicle-dialog">
          <DialogHeader><DialogTitle className="text-[#EF4444]" style={{ fontFamily: 'Outfit' }}>Supprimer le vehicule</DialogTitle></DialogHeader>
          {deleteConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-[#0A2540]">
                Etes-vous sur de vouloir supprimer <span className="font-bold">{deleteConfirm.brand} {deleteConfirm.model}</span> ({deleteConfirm.plate_number}) ?
              </p>
              <p className="text-xs text-[#64748B]">Cette action est irreversible.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 border-[#CBD5E1]">Annuler</Button>
                <Button onClick={confirmDelete} disabled={deleteLoading} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-delete-vehicle">
                  {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
